/**
 * 방 옵저빙 영상 presigned 업로드 재개용 IndexedDB 저장소.
 *
 * 토큰·presigned URL·녹화 blob 을 페이지 새로고침 전에 디스크에 두고,
 * 복귀 시 S3 PUT 을 이어한 뒤 recordVideo / complete API 를 호출한다.
 */

import type { MovingItemRequest } from '@/entities/visit-counsel/model/room-observing.types';
import type { EditTarget } from '@/features/visit-counsel/room-observing-flow/ui/EditTargetSelector';

const DB_NAME = 'moving-visit-admin-observing-video-upload';
const DB_VERSION = 1;
const STORE_JOBS = 'jobs';

/** presigned PUT 대기 (blob 보관) */
export type PendingObservingVideoUploadPhase = 'awaiting_presigned_upload' | 'presigned_uploaded';

export interface PendingObservingVideoUploadRecord {
  id: string;
  visitCounselId: string;
  sessionId: string;
  phase: PendingObservingVideoUploadPhase;
  structuredToken: string;
  uploadUrl: string;
  contentType: string;
  /** `presigned_uploaded` 단계에서는 제거됨 */
  blob?: Blob;
  createdAt: number;
  itemsPersistedBeforeUpload: boolean;
  isAdditionalSpaceMoving: boolean;
  isFirstStart: boolean;
  saveItems: boolean;
  editTarget?: EditTarget;
  isVideoOnlyForAdditionalSpace: boolean;
  items: MovingItemRequest[];
}

const MAX_JOB_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function buildObservingVideoUploadJobId(
  visitCounselId: string,
  sessionId: string
): string {
  return `${visitCounselId}:${sessionId}`;
}

function openDB(): Promise<IDBDatabase> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('window undefined'));
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_JOBS)) {
        db.createObjectStore(STORE_JOBS, { keyPath: 'id' });
      }
    };
  });
}

function isStale(createdAt: number): boolean {
  return Date.now() - createdAt > MAX_JOB_AGE_MS;
}

export async function savePendingObservingVideoUpload(
  record: PendingObservingVideoUploadRecord
): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_JOBS, 'readwrite');
      tx.objectStore(STORE_JOBS).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* ignore — 업로드는 메모리 경로로 계속 시도 */
  }
}

export async function updatePendingObservingVideoUpload(
  id: string,
  patch: Partial<Pick<PendingObservingVideoUploadRecord, 'phase' | 'structuredToken' | 'uploadUrl' | 'blob'>>
): Promise<void> {
  const existing = await getPendingObservingVideoUpload(id);
  if (!existing) return;
  await savePendingObservingVideoUpload({ ...existing, ...patch });
}

export async function getPendingObservingVideoUpload(
  id: string
): Promise<PendingObservingVideoUploadRecord | null> {
  try {
    const db = await openDB();
    const record = await new Promise<PendingObservingVideoUploadRecord | undefined>(
      (resolve, reject) => {
        const tx = db.transaction(STORE_JOBS, 'readonly');
        const req = tx.objectStore(STORE_JOBS).get(id);
        req.onsuccess = () => resolve(req.result as PendingObservingVideoUploadRecord | undefined);
        req.onerror = () => reject(req.error);
      }
    );
    db.close();
    if (!record || isStale(record.createdAt)) {
      if (record) await deletePendingObservingVideoUpload(id);
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

export async function listPendingObservingVideoUploadsForCounsel(
  visitCounselId: string
): Promise<PendingObservingVideoUploadRecord[]> {
  try {
    const db = await openDB();
    const all = await new Promise<PendingObservingVideoUploadRecord[]>((resolve, reject) => {
      const tx = db.transaction(STORE_JOBS, 'readonly');
      const req = tx.objectStore(STORE_JOBS).getAll();
      req.onsuccess = () => resolve((req.result ?? []) as PendingObservingVideoUploadRecord[]);
      req.onerror = () => reject(req.error);
    });
    db.close();
    const fresh: PendingObservingVideoUploadRecord[] = [];
    for (const record of all) {
      if (record.visitCounselId !== visitCounselId) continue;
      if (isStale(record.createdAt)) {
        await deletePendingObservingVideoUpload(record.id);
        continue;
      }
      fresh.push(record);
    }
    return fresh;
  } catch {
    return [];
  }
}

export async function deletePendingObservingVideoUpload(id: string): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_JOBS, 'readwrite');
      tx.objectStore(STORE_JOBS).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    /* ignore */
  }
}
